import { useState } from "react";
import useTableauxAmortissement from "../../hooks/use-tableaux-amortissement";
import { Table } from "../UI/Table";
import Button from "../UI/Button";
import Card from "../UI/Card";
import Text from "../UI/Text";
// @ts-expect-error pure js
import IsLoading from "../UI/IsLoading";
import PretForm from "./PretForm";
import PretDetailView from "./PretDetailView";
import IconButton from "../UI/IconButton";
import { IconEdit, IconTrash } from "@tabler/icons-react";
// @ts-expect-error pure js
import { currencyFormatter, dateConvert } from "../../api/Functions";
import type { TableauAmortissement } from "../../models/tableauAmortissement";
import styles from "./PretList.module.css";

interface PretListProps {
  profileBailleurId: number;
}

export default function PretList({ profileBailleurId }: PretListProps) {
  const { tableaux, isLoading, deleteTableau, updateTableau, createTableau } =
    useTableauxAmortissement({ profileBailleurId });

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedTableau, setSelectedTableau] =
    useState<TableauAmortissement | null>(null);

  if (isLoading) return <IsLoading />;

  async function handleDelete(tableauId: number) {
    if (window.confirm("Voulez-vous vraiment supprimer ce prêt ?")) {
      await deleteTableau(tableauId);
    }
  }

  function handleEdit(tableau: TableauAmortissement) {
    setSelectedTableau(tableau);
    setIsFormOpen(true);
  }

  function handleCreate() {
    setSelectedTableau(null);
    setIsFormOpen(true);
  }

  function handleViewDetail(tableau: TableauAmortissement) {
    setSelectedTableau(tableau);
    setIsDetailOpen(true);
  }

  async function handleSubmit(data: Partial<TableauAmortissement>) {
    if (selectedTableau) {
      await updateTableau(selectedTableau.id, data);
    } else {
      await createTableau(data);
    }
  }

  return (
    <>
      <PretForm
        profileBailleurId={profileBailleurId}
        initialData={selectedTableau}
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        onSubmit={handleSubmit}
      />

      {selectedTableau && (
        <PretDetailView
          tableau={selectedTableau}
          open={isDetailOpen}
          onOpenChange={setIsDetailOpen}
        />
      )}

      <Card>
        <div className="text-right marginB20">
          <Button onClick={handleCreate}>Nouveau prêt</Button>
        </div>

        {tableaux.length === 0 ? (
          <div className="text-center padding20">
            <Text>Aucun prêt enregistré.</Text>
            <Text color="muted">Créez un nouveau prêt pour commencer.</Text>
          </div>
        ) : (
          <Table variant="card">
            <Table.Header>
              <Table.Column>Nom</Table.Column>
              <Table.Column align="right">Montant</Table.Column>
              <Table.Column align="right">Taux</Table.Column>
              <Table.Column align="right">Durée</Table.Column>
              <Table.Column align="right">Date début</Table.Column>
              <Table.Column align="right">Actions</Table.Column>
            </Table.Header>
            <Table.Body>
              {tableaux.map((tableau) => (
                <Table.Row
                  key={tableau.id}
                  onClick={() => handleViewDetail(tableau)}
                  className={styles.clickableRow}
                >
                  <Table.Cell>{tableau.nom}</Table.Cell>
                  <Table.Cell align="right">
                    {tableau.montant_total
                      ? currencyFormatter(tableau.montant_total)
                      : "-"}
                  </Table.Cell>
                  <Table.Cell align="right">
                    {tableau.taux_nominal ? `${tableau.taux_nominal}%` : "-"}
                  </Table.Cell>
                  <Table.Cell align="right">
                    {tableau.duree_mois ? `${tableau.duree_mois} mois` : "-"}
                  </Table.Cell>
                  <Table.Cell align="right">
                    {tableau.date_debut ? dateConvert(tableau.date_debut) : "-"}
                  </Table.Cell>
                  <Table.Cell align="right">
                    <div className={styles.actions}>
                      <IconButton
                        icon={IconEdit}
                        label="Modifier"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(tableau);
                        }}
                      />
                      <IconButton
                        icon={IconTrash}
                        label="Supprimer"
                        color="danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleDelete(tableau.id);
                        }}
                      />
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Card>
    </>
  );
}
